import React, { useState, useMemo, useCallback } from "react";
import { Helmet } from "react-helmet";
import {
  Box,
  Card,
  Container,
  Divider,
  FormControl,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import GenericQuizAssign from "./GenericQuizAssign";
import AssignmentIcon from "@mui/icons-material/Assignment";
import SchoolIcon from "@mui/icons-material/School";

// Lab type mapping configuration
const LAB_TYPE_MAP = {
  left: { key: 'preconfig', label: 'Preconfigured', view: 'left' },
  center: { key: 'custom', label: 'Custom', view: 'center' },
  right: { key: 'hvm', label: 'Hypervised', view: 'right' },
  justify: { key: 'svm', label: 'Shared', view: 'justify' },
  trainer: { key: 'cloud', label: 'Cloud', view: 'trainer' },
  trainer1: { key: 'wise', label: 'Wise', view: 'trainer1' },
  trainer2: { key: 'manual', label: 'Manual', view: 'trainer2' },
  trainer3: { key: 'saas', label: 'SaaS', view: 'trainer3' },
};

const QuizAssign = ({ ...rest }) => {
  const [alignment, setAlignment] = useState("left");

  // Get current lab type configuration
  const currentLabConfig = useMemo(() => {
    return LAB_TYPE_MAP[alignment] || LAB_TYPE_MAP.left;
  }, [alignment]);

  const handleLabTypeChange = useCallback((event) => {
    const alignmentType = event.target.value;
    setAlignment(alignmentType);
  }, []);

  // Lab type configurations
  const labConfigs = useMemo(() => ({
    preconfig: {
      labType: "preconfig",
      apiEndpoint: "/api/preConfigured/",
      fieldMapping: {
        nameField: "name",
        startDateField: "start_date",
        endDateField: "end_date",
      },
      dataFilter: (data) => data.filter((item) => item.application !== "custom"),
      allowedStatuses: [
        "Creating",
        "Stopping",
        "Starting",
        "Started",
        "Cordoned",
        "Start Initiated",
        "Stopped",
        "Stop Initiated",
        "Running",
      ],
    },
    custom: {
      labType: "custom",
      apiEndpoint: "/api/manageInstance/",
      fieldMapping: {
        nameField: "name",
        startDateField: "start_date",
        endDateField: "end_date",
      },
      dataFilter: (data) => data.filter((item) => item.application === "custom"),
      allowedStatuses: [
        "Creating",
        "Stopping",
        "Starting",
        "Started",
        "Cordoned",
        "Start Initiated",
        "Stopped",
        "Stop Initiated",
        "Running",
      ],
    },
    hvm: {
      labType: "hvm",
      apiEndpoint: "/api/virtualizationCustomlabs/",
      fieldMapping: {
        nameField: "name",
        startDateField: "start_date",
        endDateField: "end_date",
      },
      dataFilter: (data) => data.filter((item) => item.application !== "wise"),
      allowedStatuses: [
        "Creating",
        "Stopping",
        "Starting",
        "Started",
        "Cordoned",
        "Start Initiated",
        "Stopped",
        "Stop Initiated",
        "Running",
      ],
    },
    svm: {
      labType: "svm",
      apiEndpoint: "/api/sharedvmlabs/",
      fieldMapping: {
        nameField: "name",
        startDateField: "start_date",
        endDateField: "end_date",
      },
      dataFilter: null,
      allowedStatuses: [
        "Creating",
        "Stopping",
        "Starting",
        "Started",
        "Cordoned",
        "Start Initiated",
        "Stopped",
        "Stop Initiated",
        "Running",
      ],
    },
    cloud: {
      labType: "cloud",
      apiEndpoint: "/api/manageCloudlabs/",
      fieldMapping: {
        nameField: "lab_name",
        startDateField: "start_period",
        endDateField: "end_period",
      },
      dataFilter: null,
      allowedStatuses: [
        "Provisioning",
        "Active",
        "Running",
        "Stopped",
        "Creating",
        "Stopping",
        "Starting",
        "Started",
        "Cordoned",
        "Start Initiated",
        "Stop Initiated",
      ],
    },
    wise: {
      labType: "wise",
      apiEndpoint: "/api/wiselabs/",
      fieldMapping: {
        nameField: "name",
        startDateField: "start_date",
        endDateField: "end_date",
      },
      dataFilter: null,
      allowedStatuses: [
        "Creating",
        "Stopping",
        "Starting",
        "Started",
        "Cordoned",
        "Start Initiated",
        "Stopped",
        "Stop Initiated",
        "Running",
      ],
    },
    manual: {
      labType: "manual",
      apiEndpoint: "/api/getmanuallabdetails/",
      fieldMapping: {
        nameField: "name",
        startDateField: "start_date",
        endDateField: "end_date",
      },
      dataFilter: (data) => {
        const manualLabs = data.filter(
          (item) => item.application?.toLowerCase() === "manual"
        );
        return manualLabs.length > 0 ? manualLabs : data;
      },
      allowedStatuses: [
        "Creating",
        "Stopping",
        "Starting",
        "Started",
        "Cordoned",
        "Start Initiated",
        "Stopped",
        "Stop Initiated",
        "Running",
      ],
    },
    saas: {
      labType: "saas",
      apiEndpoint: "/api/getsaslabdetails/",
      fieldMapping: {
        nameField: "name",
        startDateField: "start_date",
        endDateField: "end_date",
      },
      dataFilter: null,
      allowedStatuses: [
        "Creating",
        "Stopping",
        "Starting",
        "Started",
        "Cordoned",
        "Start Initiated",
        "Stopped",
        "Stop Initiated",
        "Running",
      ],
    },
  }), []);

  const getCurrentConfig = useCallback(() => {
    return labConfigs[currentLabConfig.key] || labConfigs.preconfig;
  }, [currentLabConfig.key, labConfigs]);

  return (
    <>
      <Helmet>
        <title>LabAsService - Assign Assessment to Labs</title>
      </Helmet>
      <Container maxWidth={false} sx={{ py: 6, pb: 8, px: { xs: 2, sm: 3, md: 4, lg: 5, xl: 6 } }}>
        <Card {...rest} sx={{ mb: 4 }}>
          {/* Hero Header */}
          <Box
            sx={{
              background: 'linear-gradient(135deg, rgba(46,125,50,0.12), rgba(21,101,192,0.12))',
              borderTopLeftRadius: 12,
              borderTopRightRadius: 12,
              p: { xs: 2.5, md: 3 },
            }}
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <AssignmentIcon color="primary" sx={{ fontSize: 32 }} />
              <Box>
                <Typography variant="h4" component="h1">
                  Assign Assessment to Labs
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Select a lab type and assign assessments to {currentLabConfig.label.toLowerCase()} labs
                </Typography>
              </Box>
            </Stack>
          </Box>

          <Divider />

          {/* Controls Section */}
          <Box sx={{ px: { xs: 2.5, md: 3 }, py: 2.5 }}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              alignItems={{ xs: 'stretch', sm: 'center' }}
              sx={{ mb: 3 }}
            >
              {/* Lab Type Selector */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: { sm: 280 } }}>
                <SchoolIcon color="primary" />
                <Typography
                  sx={{
                    whiteSpace: 'nowrap',
                    fontWeight: 600,
                    fontSize: '1rem',
                    color: 'text.primary',
                  }}
                >
                  Lab Type
            </Typography>
                <FormControl size="small" sx={{ minWidth: 200 }}>
              <Select
                labelId="labType-label"
                id="labType"
                    value={alignment}
                    onChange={handleLabTypeChange}
                    sx={{
                      backgroundColor: 'white',
                      '& .MuiSelect-select': {
                        py: 1,
                      },
                    }}
                  >
                    {Object.entries(LAB_TYPE_MAP).map(([value, config]) => (
                      <MenuItem key={value} value={value}>
                        {config.label}
                      </MenuItem>
                    ))}
              </Select>
            </FormControl>
              </Box>
            </Stack>

            {/* GenericQuizAssign Component */}
            {alignment ? (
              <Box sx={{ pt: 0 }}>
                <GenericQuizAssign config={getCurrentConfig()} />
              </Box>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: 300,
                  color: 'text.secondary',
                }}
              >
                <SchoolIcon sx={{ fontSize: 64, mb: 2, opacity: 0.5 }} />
                <Typography variant="h6" gutterBottom>
                  Select a Lab Type
                </Typography>
                <Typography variant="body2" align="center" sx={{ maxWidth: 400, mt: 1 }}>
                  Please select a lab type from the dropdown above to begin assigning assessments.
                </Typography>
                  </Box>
                )}
              </Box>
        </Card>
      </Container>
    </>
  );
};

export default QuizAssign;
